import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Shield, MailCheck, RefreshCw, ArrowLeft } from "lucide-react";
import { useState } from "react";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { logSecurityEvent } from "@/utils/audit.functions";

const credentialsSchema = z.object({
  email: z.string().trim().email("Enter a valid email").max(255),
  password: z
    .string()
    .min(8, "At least 8 characters")
    .max(128, "Password too long")
    .regex(/[A-Z]/, "Add an uppercase letter")
    .regex(/[a-z]/, "Add a lowercase letter")
    .regex(/[0-9]/, "Add a number"),
  name: z.string().trim().min(1, "Name required").max(80).optional(),
});

export const Route = createFileRoute("/auth")({
  head: () => ({
    meta: [
      { title: "Sign in — LendyTrack" },
      { name: "description", content: "Sign in to LendyTrack to access your dashboard, saved analyses, and AI coach." },
      { property: "og:title", content: "Sign in — LendyTrack" },
      { property: "og:url", content: "/auth" },
    ],
    links: [{ rel: "canonical", href: "/auth" }],
  }),
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => ({
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
  }),
  component: AuthPage,
});

function safeRedirect(path: string | undefined): string {
  // only allow same-origin path redirects
  if (!path) return "/dashboard";
  if (!path.startsWith("/") || path.startsWith("//")) return "/dashboard";
  return path;
}

const LOCKOUT_KEY = "lg_auth_attempts";
const MAX_ATTEMPTS = 5;
const LOCKOUT_MS = 15 * 60 * 1000;

function readAttempts(): { count: number; lockedUntil: number } {
  try {
    const raw = localStorage.getItem(LOCKOUT_KEY);
    if (!raw) return { count: 0, lockedUntil: 0 };
    return JSON.parse(raw);
  } catch {
    return { count: 0, lockedUntil: 0 };
  }
}
function writeAttempts(state: { count: number; lockedUntil: number }) {
  try { localStorage.setItem(LOCKOUT_KEY, JSON.stringify(state)); } catch {}
}
function clearAttempts() {
  try { localStorage.removeItem(LOCKOUT_KEY); } catch {}
}

function AuthPage() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const redirectTo = safeRedirect(search.redirect);
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [forgotMode, setForgotMode] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resending, setResending] = useState(false);


  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setNotice(null);

    if (forgotMode) {
      const emailParsed = z.string().trim().email().max(255).safeParse(email);
      if (!emailParsed.success) { setError("Enter a valid email"); return; }
      setLoading(true);
      try {
        await supabase.auth.resetPasswordForEmail(emailParsed.data, {
          redirectTo: `${window.location.origin}/reset-password`,
        });
        setNotice("If an account exists for that email, a reset link is on its way.");
      } finally {
        setLoading(false);
      }
      return;
    }

    const attempts = readAttempts();
    if (attempts.lockedUntil > Date.now()) {
      const mins = Math.ceil((attempts.lockedUntil - Date.now()) / 60000);
      setError(`Too many attempts. Try again in ${mins} minute${mins === 1 ? "" : "s"}.`);
      return;
    }

    const parsed = credentialsSchema.safeParse({
      email,
      password,
      name: mode === "signup" ? name : undefined,
    });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "Check your inputs");
      return;
    }

    setLoading(true);
    try {
      if (mode === "signup") {
        const { error } = await supabase.auth.signUp({
          email: parsed.data.email,
          password: parsed.data.password,
          options: {
            emailRedirectTo: `${window.location.origin}/dashboard`,
            data: { full_name: parsed.data.name },
          },
        });
        if (error) throw error;
        clearAttempts();
        setUnverifiedEmail(parsed.data.email);

      } else {
        const { error } = await supabase.auth.signInWithPassword({
          email: parsed.data.email,
          password: parsed.data.password,
        });
        if (error) throw error;
        clearAttempts();
        navigate({ to: redirectTo });
      }
    } catch (err: any) {
      const msg = String(err?.message ?? "").toLowerCase();
      const code = String(err?.code ?? "").toLowerCase();
      const notConfirmed =
        code.includes("email_not_confirmed") ||
        msg.includes("email not confirmed") ||
        msg.includes("confirm your email");

      if (mode === "signin" && notConfirmed) {
        setUnverifiedEmail(parsed.data.email);
        return;
      }

      if (mode === "signin") {
        const next = { count: attempts.count + 1, lockedUntil: 0 };
        if (next.count >= MAX_ATTEMPTS) {
          next.lockedUntil = Date.now() + LOCKOUT_MS;
          next.count = 0;
          logSecurityEvent({
            data: {
              event_type: "auth.signin.failed_lockout",
              details: { email, maxAttempts: MAX_ATTEMPTS },
            },
          }).catch(() => {});
        }
        writeAttempts(next);
      }
      setError(mode === "signin" ? "Invalid email or password" : err.message ?? "Sign up failed");
    } finally {
      setLoading(false);
    }
  };

  const resendVerification = async () => {
    if (!unverifiedEmail || resendCooldown > 0) return;
    setResending(true);
    setError(null);
    setNotice(null);
    try {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email: unverifiedEmail,
        options: { emailRedirectTo: `${window.location.origin}/dashboard` },
      });
      if (error) throw error;
      setNotice("Verification email resent. Check your inbox.");
      setResendCooldown(60);
      const iv = setInterval(() => {
        setResendCooldown((s) => {
          if (s <= 1) { clearInterval(iv); return 0; }
          return s - 1;
        });
      }, 1000);
    } catch (err: any) {
      const msg = String(err?.message ?? "");
      if (/rate|too many|seconds/i.test(msg)) {
        setError("Please wait a minute before requesting another email.");
      } else {
        setError(msg || "Could not resend verification email.");
      }
    } finally {
      setResending(false);
    }
  };


  const signInGoogle = async () => {
    setError(null);
    setLoading(true);
    try {
      // Remember intended destination — redirect_uri must be a public same-origin URL,
      // not a protected route like /dashboard, or the post-OAuth callback bounces
      // before the Supabase session is hydrated.
      try { sessionStorage.setItem("lg_post_auth_redirect", redirectTo); } catch {}
      const result = await lovable.auth.signInWithOAuth("google", {
        redirect_uri: window.location.origin,
      });
      if (result.error) {
        setError(result.error instanceof Error ? result.error.message : String(result.error));
        return;
      }
      if (result.redirected) return;
      // Popup/iframe flow: session already set on this page.
      navigate({ to: redirectTo });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="relative hidden bg-gradient-hero p-12 text-primary-foreground lg:flex lg:flex-col lg:justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="grid h-10 w-10 place-items-center rounded-lg bg-accent">
            <Shield className="h-5 w-5 text-primary" />
          </div>
          <span className="font-display text-lg font-semibold">LendyTrack</span>
        </Link>
        <div>
          <h2 className="font-display text-4xl font-semibold leading-tight">
            "LendyTrack caught a 187% APR before I signed. Saved me $2,400."
          </h2>
          <p className="mt-4 text-primary-foreground/70">— Renee P., verified user</p>
        </div>
        <div className="text-xs text-primary-foreground/60">© LendyTrack · Consumer protection, by design.</div>
      </div>

      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          {unverifiedEmail ? (
            <div>
              <div className="grid h-14 w-14 place-items-center rounded-2xl bg-primary/10 text-primary">
                <MailCheck className="h-7 w-7" />
              </div>
              <h1 className="mt-5 font-display text-3xl font-semibold tracking-tight">Verify your email</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                We sent a verification link to{" "}
                <span className="font-semibold text-foreground">{unverifiedEmail}</span>.
                Click the link in that email to activate your account, then come back to sign in.
              </p>
              <p className="mt-3 text-xs text-muted-foreground">
                Didn't get it? Check spam, or resend below. Links expire after 24 hours.
              </p>
              {error && <div className="mt-4 rounded-md bg-danger/10 px-3 py-2 text-xs text-danger">{error}</div>}
              {notice && <div className="mt-4 rounded-md bg-success/10 px-3 py-2 text-xs text-success">{notice}</div>}
              <button
                type="button"
                onClick={resendVerification}
                disabled={resending || resendCooldown > 0}
                className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary py-2.5 text-sm font-semibold text-primary-foreground shadow-soft hover:opacity-90 disabled:opacity-60"
              >
                <RefreshCw className={`h-4 w-4 ${resending ? "animate-spin" : ""}`} />
                {resending
                  ? "Sending…"
                  : resendCooldown > 0
                    ? `Resend in ${resendCooldown}s`
                    : "Resend verification email"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setUnverifiedEmail(null);
                  setError(null);
                  setNotice(null);
                  setMode("signin");
                }}
                className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-md border border-input bg-background py-2.5 text-sm font-medium hover:bg-muted"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to sign in
              </button>
              <p className="mt-6 text-center text-xs text-muted-foreground">
                Wrong address?{" "}
                <button
                  onClick={() => { setUnverifiedEmail(null); setMode("signup"); setError(null); setNotice(null); }}
                  className="font-medium text-primary hover:underline"
                >
                  Use a different email
                </button>
              </p>
            </div>
          ) : (
          <>

          <h1 className="font-display text-3xl font-semibold tracking-tight">
            {forgotMode ? "Reset your password" : mode === "signin" ? "Welcome back" : "Create your account"}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {forgotMode
              ? "Enter your email and we'll send a reset link."
              : mode === "signin" ? "Sign in to access your dashboard." : "Free forever. Upgrade any time."}
          </p>

          {!forgotMode && (
            <>
              <button
                type="button"
                onClick={signInGoogle}
                className="mt-6 flex w-full items-center justify-center gap-2 rounded-md border border-input bg-background py-2.5 text-sm font-medium hover:bg-muted"
              >
                <svg className="h-4 w-4" viewBox="0 0 48 48" aria-hidden="true">
                  <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.8 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.5 6.2 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.4-.4-3.5z"/>
                  <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16 19 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C34.5 6.2 29.5 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
                  <path fill="#4CAF50" d="M24 44c5.3 0 10.1-2 13.7-5.3l-6.3-5.3C29.3 35 26.7 36 24 36c-5.3 0-9.7-3.2-11.3-7.7l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
                  <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4-4 5.3l6.3 5.3C40.8 35.9 44 30.5 44 24c0-1.2-.1-2.4-.4-3.5z"/>
                </svg>
                Continue with Google
              </button>

              <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground">
                <div className="h-px flex-1 bg-border" /> or email <div className="h-px flex-1 bg-border" />
              </div>
            </>
          )}

          <form className="space-y-4" onSubmit={onSubmit}>
            {!forgotMode && mode === "signup" && (
              <div>
                <label className="text-xs font-medium uppercase tracking-widest text-muted-foreground">Full name</label>
                <input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring" placeholder="Alex Morgan" />
              </div>
            )}
            <div>
              <label className="text-xs font-medium uppercase tracking-widest text-muted-foreground">Email</label>
              <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring" placeholder="you@example.com" />
            </div>
            {!forgotMode && (
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium uppercase tracking-widest text-muted-foreground">Password</label>
                  {mode === "signin" && (
                    <button type="button" onClick={() => { setForgotMode(true); setError(null); setNotice(null); }} className="text-xs text-primary hover:underline">
                      Forgot?
                    </button>
                  )}
                </div>
                <input required minLength={8} type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring" placeholder="••••••••" />
              </div>
            )}
            {error && <div className="rounded-md bg-danger/10 px-3 py-2 text-xs text-danger">{error}</div>}
            {notice && <div className="rounded-md bg-success/10 px-3 py-2 text-xs text-success">{notice}</div>}
            <button disabled={loading} className="w-full rounded-md bg-primary py-2.5 text-sm font-semibold text-primary-foreground shadow-soft hover:opacity-90 disabled:opacity-60">
              {loading ? "Please wait…" : forgotMode ? "Send reset link" : mode === "signin" ? "Sign in" : "Create account"}
            </button>
          </form>

          <div className="mt-6 text-center text-sm text-muted-foreground">
            {forgotMode ? (
              <button onClick={() => { setForgotMode(false); setError(null); setNotice(null); }} className="font-medium text-primary hover:underline">
                Back to sign in
              </button>
            ) : mode === "signin" ? (
              <>Don't have an account?{" "}
                <button onClick={() => setMode("signup")} className="font-medium text-primary hover:underline">Sign up</button>
              </>
            ) : (
              <>Already a member?{" "}
                <button onClick={() => setMode("signin")} className="font-medium text-primary hover:underline">Sign in</button>
              </>
            )}
          </div>
          </>
          )}
        </div>

      </div>
    </div>
  );
}
